/** English (source); keep keys stable across locales. */
export default {
  loading: '…',
  lang: {
    en: 'English',
    de: 'Deutsch',
    ru: 'Русский',
    fr: 'Français'
  },
  nav: {
    tools: 'Tools',
    connect: 'Connect',
    discord: 'Discord',
    signIn: 'Sign in with Steam',
    signOut: 'Log out'
  },
  app: {
    runTime: 'Run time {time}',
    titleBreeder: 'rosi.wtf | Breeder',
    titleProgress: '{pct}% | GEN.{gen}'
  },
  logo: {
    plantTooltip:
      'Select a type of plant to keep track of what you are crossbreeding. Selection <strong>does not</strong> impact the results of the calculation.'
  },
  sim: {
    eyebrow: 'rosi.wtf | Breeder',
    title: 'Crossbreeding planner',
    lead:
      'Paste one six-letter gene line per plant. If your browser allows it, you can capture the game window and read genes with OCR. No install needed.',
    calculate: 'Calculate',
    stop: 'Stop',
    skip: 'Skip',
    skipTooltip: 'Skip early to the next generation (faster, may miss some paths).',
    emptyTitle: 'No viable crosses from this gene list',
    emptyBody: 'Add more plants or adjust scores in Settings. Stronger source genes usually surface better paths.',
    browserGen2:
      'The Sapling you selected comes from the <strong>2nd</strong> generation. You will need to crossbreed the Saplings that it requires first. Click on <span class="simulator_highlight-guide">highlighted</span> Saplings to see how to crossbreed them.',
    browserGen3:
      'The Sapling you selected comes from the <strong>3rd</strong> generation. You will need to crossbreed the Saplings that it requires first. Click on <span class="simulator_highlight-guide">highlighted</span> Saplings to see how to crossbreed them.',
    browserGeoBefore: 'Place plants with an "E" next to the ',
    browserGeoAfter:
      ' icon in the eastern most slots of the planter box, and plants with a "W" in the western most slots to influence the crossbreeding for the desired result.'
  },
  gene: {
    current: 'Current',
    saved: 'Saved',
    copied: 'Copied!',
    copyShare: 'Copy share link',
    save: 'Save',
    geneSequences: 'Gene sequences',
    geneHint: 'One plant per line: six letters G, H, Y, W, or X.'
  },
  results: {
    labelG: 'No. of Gs',
    labelY: 'No. of Ys',
    labelH: 'No. of Hs',
    geneN: 'Gene {n}',
    ariaIncG: 'Increase G count',
    ariaDecG: 'Decrease G count',
    ariaIncY: 'Increase Y count',
    ariaDecY: 'Decrease Y count',
    ariaIncH: 'Increase H count',
    ariaDecH: 'Decrease H count',
    conflict: 'Your filters clash: {detail}',
    alreadyOwned: 'You already have this clone at #{n} in your gene list',
    noResults: 'Filters you applied return no results!',
    clickCard: 'Click on a Card to see more details about generations and crossbreeding!',
    pressHold: 'Press and hold a Card to view alternative variants.',
    showing: 'Showing {shown} of {total} results. Tighten filters if you need fewer.',
    ruleMinZero: 'Minimum is zero..',
    ruleMaxSix: 'No more than six...',
    ruleWhole: 'Must be a whole number...'
  },
  map: {
    highlightedTitle: 'Highlighted Result',
    clearHighlight: 'Clear Highlight',
    tipGeoBefore: 'Place plants with an "E" next to the ',
    tipGeoAfter:
      ' icon in the eastern most slots of the planter box, and plants with a "W" in the western most slots to influence the crossbreeding for the desired result.',
    learnMore: 'learn more',
    tipGen2:
      'The Sapling you selected comes from the <strong>2nd</strong> generation. To be able to crossbreed it, first you will need to acquire Saplings that it requires. Click on <span class="highlighted-map_highlight-guide">highlighted</span> Saplings to see how to crossbreed them.',
    tipGen3:
      'The Sapling you selected comes from the <strong>3rd</strong> generation. To be able to crossbreed it, first you will need to acquire Saplings that it requires. Click on <span class="highlighted-map_highlight-guide">highlighted</span> Saplings to see how to crossbreed them.',
    numberHelp: '<strong>#NUMBER</strong> indicates position of the Sapling in the gene list you provided.'
  },
  browser: {
    helperVariants: 'Here you can see all the different ways you can crossbreed the same selected Sapling.',
    optionBest: 'Best Option',
    optionAlt: 'Alternative Option {n}'
  },
  previous: {
    empty: "You don't have any saved gene sets.",
    genesCount: 'Genes: {n}'
  },
  about: {
    trigger: 'About',
    title: 'About',
    fork:
      '<strong>rosi.wtf | Breeder</strong> is a fork of the older <strong>rustbreeder.com</strong> web app, restyled and adjusted to fit <a href="https://rosi.wtf" target="_blank" rel="noopener noreferrer">rosi.wtf</a>. The genetics logic and much of the workflow come from that lineage (<a href="https://github.com/wojciechSlodziak/rust-breeder" target="_blank" rel="noopener noreferrer">source on GitHub</a>).',
    fromOriginal: 'From the original author:',
    hi: "Hi. I hope you're enjoying the app :).",
    because: 'I made it, because crossbreeding every wipe without an app was exhausting.',
    feedback:
      'If you have some feedback or need help, you can reach me on my <a href="https://discord.gg/Va5475pqFw" target="_blank" rel="noopener noreferrer">Discord server</a>.',
    donate:
      'If you would like to show your support for my work and help cover the expenses, you can <a href="https://donorbox.org/rust-crossbreeding-optimizer?default_interval=o" target="_blank" rel="noopener noreferrer">donate</a>.',
    fine: 'Rust Breeder is not affiliated with Facepunch.',
    close: 'Close'
  },
  options: {
    title: 'Settings',
    reset: 'Reset',
    tabCross: 'Crossbreeding',
    tabUi: 'UI & Sounds',
    performance: 'Performance',
    workers: 'Number of Workers',
    workersHint: '~65% of cores balances speed and thermals. Raise for max speed, lower for lighter load.',
    breeding: 'Breeding',
    saplingsRange: 'Saplings Range',
    saplingsRangeHint: '2–5 is the sweet spot between effectiveness and speed.',
    generations: 'Generations',
    saplingsNextGen: 'Saplings added to next Generation',
    saplingsNextGenHint: 'Best results from current gen carried into the next calculation.',
    withRepetitions: 'Check combinations with repetitions',
    withRepetitionsHint: 'Also checks combos where one plant is used more than once. Slightly slower.',
    geneScores: 'Gene Scores',
    advanced: 'Advanced',
    manualMinScore: 'Manual Minimum Tracked Score',
    manualMinScoreHint: 'Lower values use more memory.',
    minimumTrackedScore: 'Minimum Tracked Score',
    general: 'General',
    soundsOn: 'Sounds: On',
    soundsOff: 'Sounds: Off',
    autoSaveOn: 'Auto-save genes: On',
    autoSaveOff: 'Auto-save genes: Off',
    autoSaveHint: 'Off enables a manual save button for your gene sets.',
    scanner: 'Scanner',
    skipGuideYes: 'Skip Scanning Guide: Yes',
    skipGuideNo: 'Skip Scanning Guide: No',
    calibrationHeader: 'Screen capture alignment',
    calibrationLead: "Adjust if gene letters don't line up with your resolution or Rust UI scale.",
    horizontalNudge: 'Horizontal nudge',
    horizontalHint: 'Shifts capture strips left/right.',
    verticalNudge: 'Vertical nudge',
    verticalHint: 'Shifts strips up/down.',
    geneBoxScale: 'Gene box scale',
    geneBoxHint: '1.0 = default.',
    resetAlignment: 'Reset alignment',
    close: 'Close',
    set: 'Set',
    setTooltip: 'Apply for this session only.',
    save: 'Save',
    saveTooltip: 'Apply and remember in this browser.',
    ariaInc: 'Increase',
    ariaDec: 'Decrease',
    genTickOne: 'one',
    genTickTwo: 'two',
    genTickThree: 'three',
    geneScoreRule: 'It has to be a number between -1 and 1.',
    saplingsAddedRule: 'Must be a positive integer.',
    minTrackedRule: 'Acceptable range: -6 to 6.'
  },
  scan: {
    scanRust: 'Scan Rust',
    stopScanning: 'Stop Scanning',
    initializing: 'Initializing...',
    dialogTitle: 'How to Scan Rust for Saplings?',
    displayPreview: 'Display Preview',
    close: 'Close',
    scan: 'Scan',
    li1:
      'For best results, use <strong>Chrome</strong>, <strong>Edge</strong> or <strong>Firefox</strong>. Other browsers might not work correctly.',
    li2:
      'Make sure your <strong>USER INTERFACE SCALE</strong> setting is set to <strong>1.0</strong> in Rust. Sometimes the slider shows 1.0, but the value could be at 0.97. Run <strong>graphics.uiscale</strong> command in the Rust console to see the actual value.',
    li3:
      'Make sure you run the game in <strong>16:9</strong> aspect ratio resolution (1600x900, 1920x1080, 2560x1440 or 4K) and <strong>borderless</strong> mode. If you have a different resolution on your monitor, set Rust to <strong>windowed</strong> mode and select a 16:9 ratio resolution.',
    li4: 'Click <strong>SCAN</strong> and select Rust window.',
    li5a: 'After the scanning begins, you need to display the genes on the screen by either:',
    li5b1: 'clicking on a Sapling in your inventory or storage,',
    li5b2: 'looking at a planted Sapling.',
    li5c: 'It takes about a second to capture each Sapling.',
    li6:
      "Enjoy! If it doesn't work, let me know on Discord! You can also try scanning with <strong>Display Preview</strong> active to find out what is wrong."
  },
  mapCard: {
    score: 'Score:',
    chance: 'Chance:',
    centerLabel: 'Center Sapling:',
    centerAnyExtra: 'any extra plant of same type',
    tooltipCenter1:
      'Sapling that takes the genes from Surrounding Saplings during Crossbreeding stage. Plant it in the center and let it grow alone until it reaches about 50% progress in the <strong>Sapling</strong> stage. After that, plant the Surrounding Saplings around it.',
    tooltipCenter2:
      'It is always an <strong>extra plant</strong>, so if the app shows <strong>five</strong> Surrounding Saplings you will have to plant <strong>six</strong> plants in total.',
    tooltipCenter3Exact:
      'You have to plant the exact plant that the app tells you to, otherwise crossbreeding will not work as expected.',
    tooltipCenter4Genes:
      "The genes of the plant don't matter. Using one with more Gs will help you finish the process quicker as it grows faster.",
    tooltipCenter5SameType:
      'Term "same type" means that if you are crossbreeding yellow berries, then your center seed/clone <strong>has to be</strong> a yellow berry and if you are planting potatoes then <strong>you have to</strong> plant a potato seed/clone.',
    surroundingLabel: 'Surrounding Saplings:',
    tooltipSurrounding1:
      'Saplings that have be planted around the Center Sapling before it goes into <strong>Crossbreeding</strong> stage. All Saplings have to be of the same type.',
    tooltipSurrounding2East:
      'Place your Surrounding Saplings around the Center Sapling according to the EAST/WEST guidance.',
    tooltipSurrounding2NoEast:
      'It does not matter where you place your Surrounding Saplings if your Center Sapling is in the middle of the planter.',
    tooltipSurrounding3:
      'Center Sapling will reach horizontally, vertically and diagonally during the crossbreeding phase. You are allowed to use any and all of the eight remaining planter slots to plant your Surrounding Saplings.'
  }
};
